/**
 * Ads List Page
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  InputAdornment,
  Card,
  IconButton,
  Avatar,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { PageHeader, DataTable, Column, ConfirmDialog } from '../../components/common';
import {
  useGetAdsAdminQuery,
  useDeleteAdMutation,
  Ad,
} from '../../store/api/adsApi';
import { usePermissions } from '../../hooks/usePermissions';
import moment from 'moment';
import toast from 'react-hot-toast';

const AdsListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState<boolean | undefined>(undefined);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedAd, setSelectedAd] = useState<Ad | null>(null);

  // Permission checks
  const canManageAds = hasPermission('manage_ads');
  const canViewAdDetails = hasPermission('view_ad_details');
  const canDeleteAds = hasPermission('delete_ads');

  const { data, isLoading, error } = useGetAdsAdminQuery({
    page: page + 1,
    page_size: rowsPerPage,
    search: search || undefined,
    is_active: isActiveFilter,
  });

  const [deleteAd, { isLoading: isDeleting }] = useDeleteAdMutation();

  const handleDelete = async () => {
    if (!selectedAd) return;

    try {
      await deleteAd(selectedAd.id).unwrap();
      toast.success(t('ads.toast.deleteSuccess'));
      setDeleteDialog(false);
      setSelectedAd(null);
    } catch (err: any) {
      toast.error(err?.data?.message || t('ads.toast.deleteError'));
    }
  };

  const columns: Column<Ad>[] = [
    {
      id: 'image',
      label: t('ads.cols.image'),
      minWidth: 100,
      render: (row) => (
        <Avatar
          src={row.image || undefined}
          alt={row.title}
          variant="rounded"
          sx={{ width: 60, height: 60 }}
        >
          <ImageIcon />
        </Avatar>
      ),
    },
    {
      id: 'title',
      label: t('ads.cols.title'),
      minWidth: 200,
      render: (row) => (
        <Box>
          <Box sx={{ fontWeight: 600, color: 'primary.main' }}>{row.title}</Box>
          {row.title_ar && (
            <Box sx={{ fontSize: '0.875rem', color: 'text.secondary', mt: 0.5 }}>
              {row.title_ar}
            </Box>
          )}
        </Box>
      ),
    },
    {
      id: 'description',
      label: t('ads.cols.description'),
      minWidth: 250,
      render: (row) => (
        <Box
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 300,
          }}
        >
          {row.description || '-'}
        </Box>
      ),
    },
    {
      id: 'created_at',
      label: t('ads.cols.created'),
      minWidth: 150,
      render: (row) => moment(row.created_at).format('MMM DD, YYYY'),
    },
    {
      id: 'actions',
      label: t('common.actions'),
      minWidth: 120,
      align: 'right',
      render: (row) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          {canViewAdDetails && (
            <IconButton
              size="small"
              onClick={() => navigate(`/ads/${row.id}`)}
              color="primary"
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          )}
          {canManageAds && (
            <IconButton
              size="small"
              onClick={() => navigate(`/ads/${row.id}/edit`)}
              color="primary"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}
          {canDeleteAds && (
            <IconButton
              size="small"
              onClick={() => {
                setSelectedAd(row);
                setDeleteDialog(true);
              }}
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title={t('ads.title')}
        breadcrumbs={[
          { label: t('nav.dashboard'), path: '/' },
          { label: t('nav.ads') },
        ]}
        action={
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={isActiveFilter === undefined}
                  onChange={(e) => setIsActiveFilter(e.target.checked ? undefined : true)}
                />
              }
              label={isActiveFilter === undefined ? t('ads.filters.showAll') : t('ads.filters.activeOnly')}
            />
            <TextField
              size="small"
              placeholder={t('ads.filters.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 250 }}
            />
            {canManageAds && (
              <IconButton
                color="primary"
                onClick={() => navigate('/ads/create')}
                sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}
              >
                <AddIcon />
              </IconButton>
            )}
          </Box>
        }
      />

      <Card sx={{ mt: 3 }}>
        <DataTable
          columns={columns}
          data={data?.results || []}
          loading={isLoading}
          error={error ? (typeof error === 'string' ? error : t('ads.toast.loadError')) : null}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          totalCount={data?.count || 0}
        />
      </Card>

      <ConfirmDialog
        open={deleteDialog}
        title={t('ads.dialog.deleteTitle')}
        message={t('ads.dialog.deleteMessage', { title: selectedAd?.title })}
        onConfirm={handleDelete}
        onCancel={() => {
          setDeleteDialog(false);
          setSelectedAd(null);
        }}
        loading={isDeleting}
      />
    </Box>
  );
};

export default AdsListPage;

